import { colors, brand, card, red } from './colors.js';

/**
 * App-wide scrollbar styling: every scrollbar in the app (page/body, tables,
 * modals, dropdown menus, any overflow container) uses the brand red accent.
 * Exposed as a reusable `sx` fragment so individual overflow containers that
 * need to opt in explicitly (e.g. a scoped menu Paper) can spread the same
 * rules instead of re-declaring them. Covers BOTH engines:
 *   - WebKit/Blink (Chrome, Edge, Safari) via `::-webkit-scrollbar*`
 *   - Firefox via the standard `scrollbar-color` / `scrollbar-width`
 */
export const RED_SCROLLBAR_SX = {
  scrollbarWidth: 'thin',
  scrollbarColor: `${red[700]} transparent`,
  '&::-webkit-scrollbar': { width: 10, height: 10 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: red[700],
    borderRadius: 8,
    border: '2px solid transparent',
    backgroundClip: 'padding-box',
  },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: red[800] },
};

/**
 * Design tokens layered on top of the shell-derived color palette. These
 * describe spacing, radius, typography, and shadows shared by ui-kit
 * components. Keep these consistent with the shell where they overlap.
 */
export const tokens = {
  spacing: (factor) => `${factor * 8}px`,
  radius: {
    sm: 4,
    md: 8,
    lg: 12,
  },
  shadow: {
    card: card.shadow,
    cardHover: card.shadowHover,
    raised: '0 4px 12px rgba(16, 24, 40, 0.12)',
  },
  typography: {
    fontFamily: '"Poppins", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
    kpiValue: { fontSize: '1.75rem', fontWeight: 700 },
    kpiLabel: { fontSize: '0.8125rem', fontWeight: 500 },
  },
  sidebarWidth: 273,
};

/**
 * Build the MUI theme options object from the copied palette + tokens.
 * LIGHT mode: white/light-gray surfaces with subtle borders and dark text.
 * The dark navy sidebar is styled independently (see DeptSidebar) and does not
 * follow this palette. The app wires this into a MUI ThemeProvider via
 * createTheme().
 */
export const themeOptions = {
  palette: {
    mode: 'light',
    primary: colors.primary,
    secondary: colors.secondary,
    background: colors.background,
    text: colors.text,
    divider: colors.divider,
  },
  shape: { borderRadius: tokens.radius.lg },
  spacing: 8,
  typography: {
    fontFamily: tokens.typography.fontFamily,
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // App-wide red scrollbars. The `*` rule paints every scrollable
        // element in both engines; `html`/`body` cover the page scrollbar
        // itself. Scoped overflow containers (tables, modals, dropdowns)
        // inherit this unless they explicitly override it.
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: `${red[700]} transparent`,
        },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-track': { background: 'transparent' },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: red[700],
          borderRadius: 8,
          border: '2px solid transparent',
          backgroundClip: 'padding-box',
        },
        '*::-webkit-scrollbar-thumb:hover': { backgroundColor: red[800] },
        html: {
          scrollbarWidth: 'thin',
          scrollbarColor: `${red[700]} transparent`,
        },
        body: {
          minHeight: '100vh',
          backgroundColor: brand.pageBg,
          color: brand.textPrimary,
          scrollbarWidth: 'thin',
          scrollbarColor: `${red[700]} transparent`,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: brand.paper,
          color: brand.textPrimary,
          borderBottom: `1px solid ${brand.border}`,
          boxShadow: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: brand.paper,
          backgroundImage: 'none',
          border: `1px solid ${brand.border}`,
          borderRadius: 12,
          boxShadow: 'none',
        },
      },
    },
    // App-wide table density: larger, "zoomed-in" type and tighter horizontal
    // padding between columns than the MUI default (which is 16px each side).
    // Alignment is intentionally NOT forced here — each column owns its own
    // `align` so numeric columns stay right-aligned and text columns stay
    // left-aligned, header and body together.
    MuiTableCell: {
      styleOverrides: {
        root: {
          // Tighter horizontal gap, comfortable vertical rhythm.
          padding: '8px 10px',
          fontSize: '14px',
        },
        // Header cells: slightly smaller than the body + bold, but still larger
        // than the previous default so the whole table reads "zoomed in".
        head: {
          fontSize: '13px',
          fontWeight: 700,
          lineHeight: 1.3,
        },
        body: {
          fontSize: '14px',
          lineHeight: 1.4,
        },
        // `size="small"` tables (DataTable + most dashboard tables) keep the
        // same denser padding instead of MUI's extra-compact 6px/16px.
        sizeSmall: {
          padding: '8px 10px',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          paddingInline: 20,
          transition: 'transform 150ms ease, background-color 150ms ease, box-shadow 150ms ease',
          // CTA hover lift: scale to 1.05 on hover (sitewide), honoring reduced
          // motion. Disabled buttons do not lift.
          '&:hover': { transform: 'scale(1.05)' },
          '&.Mui-disabled': { transform: 'none' },
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            '&:hover': { transform: 'none' },
          },
        },
      },
    },
  },
};
